import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, Check, Settings2, Search } from 'lucide-react';
import agentsService, { Agent, AgentAccessUser } from '../../services/agentsService';
import { PageHeader, Spinner } from '../../components/ui';
import '../../styles/theme.css';

/**
 * Admin: per-user agent-access management (users × agents). Toggling a cell grants/revokes a
 * user's access to an agent. Admins implicitly have every agent (shown but locked). Enforcement
 * is server-side; this is the management surface.
 */
const AgentAccess: React.FC = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [users, setUsers] = useState<AgentAccessUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string>('');   // `${userId}:${agentKey}` in-flight
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ agents }, access] = await Promise.all([
        agentsService.listAllAgents(),
        agentsService.listAgentAccess(1, 100, search || undefined),
      ]);
      setAgents(agents);
      setUsers(access.users);
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => { load(); }, [load]);

  const isAdmin = (u: AgentAccessUser) => u.role === 'admin' || u.role === 'super_admin';

  const toggle = async (u: AgentAccessUser, agentKey: string) => {
    if (isAdmin(u)) return;                 // admins implicitly have all agents
    const has = u.allowed_agents.includes(agentKey);
    setBusy(`${u.id}:${agentKey}`); setMsg('');
    try {
      const res = has
        ? await agentsService.revokeAgent(u.id, agentKey)
        : await agentsService.grantAgent(u.id, agentKey);
      setUsers((prev) => prev.map((x) => x.id === u.id
        ? { ...x, allowed_agents: res.allowed_agents } : x));
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusy('');
    }
  };

  return (
    <div>
      <PageHeader title="Agent access"
        subtitle="Grant each user the agents they may use. Admins implicitly have every agent." />

      <div className="mx-card mx-card-pad" style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center' }}>
        <Search size={15} className="mx-subtle" />
        <input className="mx-input" placeholder="Search users…" value={search}
          onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
        <div style={{ flex: 1 }} />
        {agents.map((a) => (
          <Link key={a.key} to={`/admin/agents/${a.key}/config`} className="mx-subtle"
            style={{ fontSize: 12, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <Settings2 size={13} /> {a.name}
          </Link>
        ))}
      </div>

      {msg && <div className="mx-muted" style={{ fontSize: 13, marginBottom: 10 }}>{msg}</div>}

      {loading ? <div className="mx-card mx-card-pad"><Spinner label="Loading…" /></div> : (
        <div className="mx-card" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left' }}>
                <th style={{ padding: '10px 14px' }}>User</th>
                <th style={{ padding: '10px 14px' }}>Role</th>
                {agents.map((a) => (
                  <th key={a.key} style={{ padding: '10px 14px', textAlign: 'center' }}>{a.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} style={{ borderTop: '1px solid var(--mx-border)' }}>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontWeight: 600 }}>{u.username}</div>
                    <div className="mx-subtle" style={{ fontSize: 11.5 }}>{u.email}</div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>{u.role}</td>
                  {agents.map((a) => {
                    const granted = isAdmin(u) || u.allowed_agents.includes(a.key);
                    const locked = isAdmin(u);
                    const key = `${u.id}:${a.key}`;
                    return (
                      <td key={a.key} style={{ padding: '10px 14px', textAlign: 'center' }}>
                        <button
                          onClick={() => toggle(u, a.key)}
                          disabled={locked || busy === key}
                          title={locked ? 'Admins have every agent' : (granted ? 'Revoke' : 'Grant')}
                          style={{
                            width: 26, height: 26, borderRadius: 6, cursor: locked ? 'default' : 'pointer',
                            border: '1px solid var(--mx-border)',
                            background: granted ? 'var(--mx-accent)' : 'transparent',
                            color: granted ? '#fff' : 'var(--mx-subtle)',
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            opacity: locked ? 0.6 : 1,
                          }}>
                          {granted ? <Check size={15} /> : ''}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mx-card mx-card-pad mx-subtle" style={{ fontSize: 11.5, marginTop: 16, display: 'flex', gap: 8, alignItems: 'center' }}>
        <ShieldCheck size={14} /> Access is enforced server-side on every agent-scoped route; a user without an agent can’t reach its runs, config, or results.
      </div>
    </div>
  );
};

export default AgentAccess;
